import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { isUnmappedJobType, jobTypeLabel } from '@/domain/registry';
import { StatusPill } from '@/components/ui';
import type { QueueJobView, UpcomingRun } from '@/api/wsr/types';
import { jobTimelineText } from './jobTimeline';
import { queueKindValue, type QueueTranslateFn } from './tableValues';

interface ActiveJobsTableProps {
  rows: QueueJobView[];
  generatedAt: string;
  emptyMessage: string;
  locale: string;
  t: QueueTranslateFn;
  showTenantColumn?: boolean;
}

export function ActiveJobsTable({
  rows,
  generatedAt,
  emptyMessage,
  locale,
  t,
  showTenantColumn = true,
}: ActiveJobsTableProps) {
  const columnCount = showTenantColumn ? 7 : 6;

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={t('queue.tableAriaLabel', 'Queue jobs')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('queue.col.id', 'ID')}</TableCell>
            {showTenantColumn ? <TableCell>{t('queue.col.tenant', 'Tenant')}</TableCell> : null}
            <TableCell>{t('queue.col.jobType', 'Job type')}</TableCell>
            <TableCell>{t('queue.col.kind', 'Kind')}</TableCell>
            <TableCell>{t('queue.col.status', 'Status')}</TableCell>
            <TableCell>{t('queue.col.timing', 'Timing')}</TableCell>
            <TableCell>{t('queue.col.progress', 'Progress')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} align="center">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((job) => {
              const unmapped = isUnmappedJobType(job.jobType, t);
              return (
                <TableRow key={job.id}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{job.id}</TableCell>
                  {showTenantColumn ? <TableCell>{job.tenantSlug}</TableCell> : null}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <span>{jobTypeLabel(job.jobType, t)}</span>
                      {unmapped ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('common.unmapped', 'Unmapped')}
                        />
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>{queueKindValue(job.runKind, t)}</TableCell>
                  <TableCell>
                    <StatusPill status={job.status} />
                  </TableCell>
                  <TableCell>{jobTimelineText(job, generatedAt, t)}</TableCell>
                  <TableCell>
                    {job.progressCurrent === null
                      ? '—'
                      : t('queue.rowsProcessed', '{{n}} rows', {
                          n: job.progressCurrent.toLocaleString(locale),
                        })}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface UpcomingTableProps {
  rows: UpcomingRun[];
  headingId: string;
  nextValue: (run: UpcomingRun) => string;
  t: QueueTranslateFn;
  showTenantColumn?: boolean;
}

export function UpcomingTable({
  rows,
  headingId,
  nextValue,
  t,
  showTenantColumn = true,
}: UpcomingTableProps) {
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-labelledby={headingId}>
        <TableHead>
          <TableRow>
            {showTenantColumn ? (
              <TableCell>{t('queue.upcoming.colTenant', 'Tenant')}</TableCell>
            ) : null}
            <TableCell>{t('queue.upcoming.colJob', 'Job')}</TableCell>
            <TableCell>{t('queue.upcoming.colNext', 'Next')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((run) => (
            <TableRow key={`${run.tenantSlug}:${run.jobType}`}>
              {showTenantColumn ? <TableCell>{run.tenantSlug}</TableCell> : null}
              <TableCell>{jobTypeLabel(run.jobType, t)}</TableCell>
              <TableCell>{nextValue(run)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
